import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import {
	Badge,
	Button,
	Card,
	Dropdown,
	Input,
	PageHeader,
	SearchableSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Textarea,
	Toggle,
	Typography
} from '@clickguard/design-system'
import {
	Survey,
	SurveyConditionType,
	SurveyDisplayScope,
	SurveyEventCountMode,
	SurveyStatus,
	SurveyTriggerCondition,
	SurveyTriggerOperator
} from 'api-models'
import { ReactNode, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AdminSurveys, SurveyInput } from 'services/admin/surveys'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'

const EVENT_OPTIONS = Object.entries(PostHogEvent).map(([key, value]) => ({
	value: value as string,
	label: value as string,
	supportingText: key
}))
const PATH_OPTIONS = [
	{ value: '', label: '— any —' },
	...Object.entries(PostHogPath).map(([key, value]) => ({
		value: value as string,
		label: `${key} (${value})`
	}))
]
const STATUS_OPTIONS = Object.values(SurveyStatus).map((s) => ({
	value: s,
	label: s
}))
const OPERATOR_OPTIONS = Object.values(SurveyTriggerOperator).map((op) => ({
	value: op,
	label: op
}))
const CONDITION_TYPE_OPTIONS = [
	{ value: SurveyConditionType.SESSION_COUNT, label: 'Distinct sessions' },
	{ value: SurveyConditionType.EVENT_COUNT, label: 'Event count' }
]
const MODE_OPTIONS = [
	{ value: SurveyEventCountMode.OCCURRENCES, label: 'occurrences' },
	{ value: SurveyEventCountMode.DISTINCT_SESSIONS, label: 'distinct sessions' }
]

const STATUS_BADGE: Record<SurveyStatus, 'success' | 'gray' | 'warning'> = {
	[SurveyStatus.ACTIVE]: 'success',
	[SurveyStatus.INACTIVE]: 'gray',
	[SurveyStatus.ARCHIVED]: 'warning'
}

const emptySurvey = (): SurveyInput => ({
	feature_name: '',
	status: SurveyStatus.INACTIVE,
	page_url: '',
	display_scope: SurveyDisplayScope.SPECIFIC_PAGE,
	trigger: {
		operator: SurveyTriggerOperator.AND,
		conditions: [
			{ type: SurveyConditionType.SESSION_COUNT, page_url: '', threshold: 2 }
		]
	},
	rating_question: '',
	scale_labels: { min: '', max: '' },
	branch_prompts: { low: '', four: '', five: '' }
})

const triggerSummary = (survey: Survey): string => {
	const parts = (survey.trigger?.conditions || []).map((c) =>
		c.type === SurveyConditionType.SESSION_COUNT
			? `${c.threshold} sessions on ${c.page_url}`
			: `${c.event}${c.path ? `/${c.path}` : ''} ×${c.threshold} (${c.mode})`
	)
	return parts.join(` ${survey.trigger?.operator} `) || '—'
}

/** Labelled Dropdown — pairs a design-system label with a full-width select. */
const SelectField = ({
	label,
	options,
	value,
	onValueChange
}: {
	label: string
	options: { value: string; label: ReactNode }[]
	value: string
	onValueChange: (value: string) => void
}) => (
	<div className="flex w-full flex-col gap-1.5">
		<Typography as="label" variant="label" color="default">
			{label}
		</Typography>
		<Dropdown
			className="w-full"
			menuWidth="trigger"
			options={options}
			value={value}
			onValueChange={onValueChange}
		/>
	</div>
)

/** Internal-only Surveys config page (DEV-2363). */
export const AdminSurveysPage = () => {
	const [surveys, setSurveys] = useState<Survey[]>([])
	const [loading, setLoading] = useState(true)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [form, setForm] = useState<SurveyInput | null>(null)
	const [saving, setSaving] = useState(false)
	const [deleteTarget, setDeleteTarget] = useState<Survey | null>(null)
	const [deleting, setDeleting] = useState(false)

	const load = async () => {
		setLoading(true)
		try {
			setSurveys(await AdminSurveys.list())
		} catch (err) {
			toast.error('Failed to load surveys')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		load()
	}, [])

	const startCreate = () => {
		setEditingId('new')
		setForm(emptySurvey())
	}

	const startEdit = (survey: Survey) => {
		setEditingId(survey.id)
		const { id, created_at, updated_at, created_by, ...input } = survey
		setForm(input)
	}

	const cancel = () => {
		setEditingId(null)
		setForm(null)
	}

	const patchForm = (patch: Partial<SurveyInput>) =>
		setForm((prev) => (prev ? { ...prev, ...patch } : prev))

	const updateCondition = (
		index: number,
		condition: SurveyTriggerCondition
	) => {
		if (!form) return
		const conditions = [...form.trigger.conditions]
		conditions[index] = condition
		patchForm({ trigger: { ...form.trigger, conditions } })
	}

	const addCondition = () => {
		if (!form) return
		patchForm({
			trigger: {
				...form.trigger,
				conditions: [
					...form.trigger.conditions,
					{
						type: SurveyConditionType.SESSION_COUNT,
						page_url: '',
						threshold: 2
					}
				]
			}
		})
	}

	const removeCondition = (index: number) => {
		if (!form) return
		patchForm({
			trigger: {
				...form.trigger,
				conditions: form.trigger.conditions.filter((_, i) => i !== index)
			}
		})
	}

	const save = async () => {
		if (!form) return
		if (!form.feature_name || !form.page_url || !form.rating_question) {
			toast.error('Feature name, page URL and rating question are required')
			return
		}
		setSaving(true)
		try {
			if (editingId === 'new') {
				await AdminSurveys.create(form)
				toast.success('Survey created')
			} else if (editingId) {
				await AdminSurveys.update(editingId, form)
				toast.success('Survey updated')
			}
			cancel()
			load()
		} catch (err: any) {
			toast.error(err?.response?.data?.message || 'Failed to save survey')
		} finally {
			setSaving(false)
		}
	}

	const remove = async () => {
		if (!deleteTarget) return
		setDeleting(true)
		try {
			await AdminSurveys.remove(deleteTarget.id)
			toast.success('Survey deleted')
			setDeleteTarget(null)
			load()
		} catch (err) {
			toast.error('Failed to delete survey')
		} finally {
			setDeleting(false)
		}
	}

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Surveys"
				subtitle="Configure in-app feedback surveys (internal only)"
				actions={
					<Button variant="primary" onClick={startCreate}>
						New survey
					</Button>
				}
			/>

			{loading ? (
				<Typography variant="body-sm" color="muted">
					Loading…
				</Typography>
			) : (
				<Card padding="none">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Feature</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Page</TableHead>
								<TableHead>Scope</TableHead>
								<TableHead>Trigger</TableHead>
								<TableHead />
							</TableRow>
						</TableHeader>
						<TableBody>
							{surveys.map((survey) => (
								<TableRow key={survey.id}>
									<TableCell>
										<Typography variant="body-sm" className="font-medium">
											{survey.feature_name}
										</Typography>
									</TableCell>
									<TableCell>
										<Badge variant={STATUS_BADGE[survey.status]} dot>
											{survey.status}
										</Badge>
									</TableCell>
									<TableCell>{survey.page_url}</TableCell>
									<TableCell>{survey.display_scope}</TableCell>
									<TableCell>
										<Typography variant="body-sm" color="muted">
											{triggerSummary(survey)}
										</Typography>
									</TableCell>
									<TableCell>
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => startEdit(survey)}
											>
												Edit
											</Button>
											<Button
												variant="danger"
												size="sm"
												onClick={() => setDeleteTarget(survey)}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
							{!surveys.length && (
								<TableRow>
									<TableCell colSpan={6}>
										<Typography variant="body-sm" color="muted">
											No surveys yet.
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Card>
			)}

			{form && (
				<Card
					header={
						<Typography variant="card-title">
							{editingId === 'new' ? 'New survey' : 'Edit survey'}
						</Typography>
					}
				>
					<div className="flex flex-col gap-4">
						<div className="grid grid-cols-2 gap-4">
							<Input
								state="default"
								label="Feature name"
								value={form.feature_name}
								onChange={(e) => patchForm({ feature_name: e.target.value })}
								placeholder="account_overview"
							/>
							<SelectField
								label="Status"
								options={STATUS_OPTIONS}
								value={form.status}
								onValueChange={(status) =>
									patchForm({ status: status as SurveyStatus })
								}
							/>
							<Input
								state="default"
								label="Page URL (trailing path segment)"
								value={form.page_url}
								onChange={(e) => patchForm({ page_url: e.target.value })}
								placeholder="account-overview"
							/>
							<div className="flex items-end">
								<Toggle
									checked={form.display_scope === SurveyDisplayScope.ANYWHERE}
									onChange={(e) =>
										patchForm({
											display_scope: e.target.checked
												? SurveyDisplayScope.ANYWHERE
												: SurveyDisplayScope.SPECIFIC_PAGE
										})
									}
									label="Show anywhere"
									description="Off = show only on the page URL above"
								/>
							</div>
						</div>

						<Input
							state="default"
							label="Rating question"
							value={form.rating_question}
							onChange={(e) => patchForm({ rating_question: e.target.value })}
						/>

						<div className="grid grid-cols-2 gap-4">
							<Input
								state="default"
								label="Scale label — low (1)"
								value={form.scale_labels.min}
								onChange={(e) =>
									patchForm({
										scale_labels: { ...form.scale_labels, min: e.target.value }
									})
								}
							/>
							<Input
								state="default"
								label="Scale label — high (5)"
								value={form.scale_labels.max}
								onChange={(e) =>
									patchForm({
										scale_labels: { ...form.scale_labels, max: e.target.value }
									})
								}
							/>
						</div>

						<div className="grid grid-cols-3 gap-4">
							<Textarea
								label="Follow-up (score 1–3)"
								value={form.branch_prompts.low}
								onChange={(e) =>
									patchForm({
										branch_prompts: {
											...form.branch_prompts,
											low: e.target.value
										}
									})
								}
							/>
							<Textarea
								label="Follow-up (score 4)"
								value={form.branch_prompts.four}
								onChange={(e) =>
									patchForm({
										branch_prompts: {
											...form.branch_prompts,
											four: e.target.value
										}
									})
								}
							/>
							<Textarea
								label="Follow-up (score 5)"
								value={form.branch_prompts.five}
								onChange={(e) =>
									patchForm({
										branch_prompts: {
											...form.branch_prompts,
											five: e.target.value
										}
									})
								}
							/>
						</div>

						{/* Trigger group */}
						<Card padding="compact" className="bg-subtle">
							<div className="flex flex-col gap-3">
								<div className="flex items-center gap-3">
									<Typography variant="label">
										Trigger — combine conditions with
									</Typography>
									<Dropdown
										options={OPERATOR_OPTIONS}
										value={form.trigger.operator}
										onValueChange={(operator) =>
											patchForm({
												trigger: {
													...form.trigger,
													operator: operator as SurveyTriggerOperator
												}
											})
										}
									/>
								</div>

								{form.trigger.conditions.map((condition, index) => (
									<Card key={index} padding="compact">
										<div className="flex flex-wrap items-end gap-3">
											<SelectField
												label="Type"
												options={CONDITION_TYPE_OPTIONS}
												value={condition.type}
												onValueChange={(value) => {
													const type = value as SurveyConditionType
													updateCondition(
														index,
														type === SurveyConditionType.SESSION_COUNT
															? { type, page_url: form.page_url, threshold: 2 }
															: {
																	type: SurveyConditionType.EVENT_COUNT,
																	event: EVENT_OPTIONS[0].value,
																	mode: SurveyEventCountMode.OCCURRENCES,
																	threshold: 1
															  }
													)
												}}
											/>

											{condition.type === SurveyConditionType.SESSION_COUNT ? (
												<Input
													state="default"
													label="Page URL"
													value={condition.page_url}
													onChange={(e) =>
														updateCondition(index, {
															...condition,
															page_url: e.target.value
														})
													}
													placeholder="account-overview"
												/>
											) : (
												<>
													<div className="flex w-64 flex-col gap-1.5">
														<Typography
															as="label"
															variant="label"
															color="default"
														>
															Event
														</Typography>
														<SearchableSelect
															className="w-full"
															options={EVENT_OPTIONS}
															value={condition.event}
															onValueChange={(event) =>
																updateCondition(index, { ...condition, event })
															}
															searchPlaceholder="Search events"
														/>
													</div>
													<SelectField
														label="Path (optional)"
														options={PATH_OPTIONS}
														value={condition.path || ''}
														onValueChange={(path) =>
															updateCondition(index, {
																...condition,
																path: path || undefined
															})
														}
													/>
													<SelectField
														label="Mode"
														options={MODE_OPTIONS}
														value={condition.mode}
														onValueChange={(mode) =>
															updateCondition(index, {
																...condition,
																mode: mode as SurveyEventCountMode
															})
														}
													/>
												</>
											)}

											<div className="w-24">
												<Input
													state="default"
													label="Threshold"
													type="number"
													min={1}
													value={condition.threshold}
													onChange={(e) =>
														updateCondition(index, {
															...condition,
															threshold: Number(e.target.value)
														})
													}
												/>
											</div>

											<Button
												variant="ghost"
												size="sm"
												onClick={() => removeCondition(index)}
											>
												Remove
											</Button>
										</div>
									</Card>
								))}

								<div>
									<Button variant="secondary" size="sm" onClick={addCondition}>
										Add condition
									</Button>
								</div>
							</div>
						</Card>

						<div className="flex gap-2">
							<Button loader="dots" variant="primary" onClick={save} isLoading={saving}>
								{editingId === 'new' ? 'Create survey' : 'Save changes'}
							</Button>
							<Button variant="ghost" onClick={cancel}>
								Cancel
							</Button>
						</div>
					</div>
				</Card>
			)}

			<ConfirmDialog
				open={!!deleteTarget}
				tone="danger"
				title="Delete survey"
				description={
					deleteTarget
						? `Delete survey “${deleteTarget.feature_name}”? This cannot be undone.`
						: ''
				}
				confirmLabel="Delete"
				loading={deleting}
				closeOnConfirm={false}
				onClose={() => setDeleteTarget(null)}
				onConfirm={remove}
			/>
		</div>
	)
}
